/**
 * Zod schemas for runtime API response validation.
 *
 * These schemas mirror the TypeScript types in ./types.ts and the backend
 * API contract in tdds/001-backend-architecture.md. If there is a
 * discrepancy, the backend TDD takes precedence.
 *
 * Every schema corresponds to a response type used by endpoint modules
 * (auth.ts, sessions.ts, messages.ts, users.ts, config.ts, profile.ts,
 * bookmarks.ts). Admin and analysis endpoints use their own fetch
 * functions and are out of scope for now.
 *
 * Exception: the UIAction wire-types section near the bottom of this
 * file has its own canonical source-of-truth (the proto file
 * `tricorder-protos/proto/tricorder/mcpui/v1/action.proto`), not the backend TDD.
 */

import { z, type ZodType } from "zod";

import type {
  ToolAction,
  IntentAction,
  PromptAction,
  LinkAction,
  NotifyAction,
  UIAction,
  UIActionRequest,
  UIActionAcknowledgment,
  UIActionAcknowledgmentStatus,
  UIActionStartedEvent,
  UIActionResultEvent,
  UIActionErrorEvent,
  ElicitationPromptEvent,
  ProtocolNodeAction,
  ProtocolNodeContent,
  ProtocolStepBlock,
  ProtocolStepBlockData,
  TrainingStepBlock,
  TrainingStepBlockData,
} from "./types";
import { UNKNOWN_BLOCK_TYPE } from "./types";

// ============================================================================
// Helpers
// ============================================================================

/**
 * Wraps a Zod schema so that JSON `null` (from Python `None`) is normalised
 * to `undefined` before validation. This keeps the inferred TypeScript type
 * free of `| null`, matching the existing frontend type definitions.
 */
function nullish<T extends ZodType>(schema: T) {
  return z.preprocess((v) => v ?? undefined, schema);
}

/**
 * The one list body (`apicontract.Page[T]`): `{"items": [...]}`. Every
 * collection response decodes through this; whether it continues is the
 * `Link: rel="next"` header, which `api.getPage` reads beside the body.
 *
 * `items` is required and non-nullable: an empty collection answers `[]`, and
 * a `null` there would be a server defect this must surface rather than paper
 * over.
 */
export function pageSchema<T extends ZodType>(item: T) {
  return z.object({
    items: z.array(item),
  });
}

// ============================================================================
// Primitive / shared schemas
// ============================================================================

const LanguageSchema = z.union([
  z.literal("de"),
  z.literal("fr"),
  z.literal("en"),
]);

// House-standard FHIR-style Reference (see @/lib/reference). All fields optional;
// a Reference with only `display` is valid (legacy free text).
const ReferenceSchema = z.object({
  reference: z.string().optional(),
  identifier: z.object({ system: z.string(), value: z.string() }).optional(),
  display: z.string().optional(),
});

// A caller's direct (admin-granted) ward membership — see location_memberships
// on UserResponseSchema below.
const LocationMembershipSchema = z.object({
  location_id: z.string(),
  name: z.string(),
  kind: z.string(),
  role: z.string(),
});

// ============================================================================
// Auth
// ============================================================================

export const UserResponseSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  // /me is tricorder-users' account since the users cut-over, and the
  // clinical profile fields (role display, years, specialisation) still live
  // in care-app's own tables — the account does not carry them yet, and a
  // required block here would fail the whole parse and bounce every page to
  // login. Absent reads as "not stated", the same fact null is.
  role: z.string().nullable().optional().default(null),
  years_experience: z.number().nullable().optional().default(null),
  specialisation: z.string().nullable().optional().default(null),
  institution: z.string().nullable().optional(),
  // Display unit/ward. Optional: older backends / unset profiles omit it.
  unit: z.string().nullable().optional(),
  // House-standard References (FHIR-style); all sub-fields optional.
  role_ref: ReferenceSchema.nullable().optional(),
  location_ref: ReferenceSchema.nullable().optional(),
  // The deployment's org-tree root; present only when the deployment has one
  // unambiguous org root. Plain optional Reference, same as role_ref/location_ref
  // above — NOT defaulted, absence means "not stated" not "no org root".
  organisation_ref: ReferenceSchema.nullable().optional(),
  language: LanguageSchema,
  // When the account became usable; null until it activates. Optional because a
  // /me served by the PREVIOUS image during a rollout or an image rollback does
  // not carry the key at all, and a bundle that refuses to parse it takes the
  // whole app down for a field nothing renders yet. Not defaulted either: an
  // absent field is "this response does not say", which is a different fact
  // from null, and inventing a date for it would be worse than admitting both.
  activated_at: z.string().nullable().optional(),
  // Same cut-over gap as role above: the account answers no twin name until
  // the profile moves or /me composes it; the app's default (the caller's
  // own name) reads the same.
  twin_name: z.string().optional().default(''),
  // The practitioner's own free-text summary of their practice. NULLABLE
  // because null is a different answer from an empty string, which is a
  // narrative she cleared. OPTIONAL for the same reason activated_at above is:
  // a /me served by the previous image during a rollout carries no such key at
  // all, and a required block would fail the whole parse of the caller's own
  // profile — every screen, not just the narrative — for as long as the two
  // images overlap. Absent means "this response does not say"; null means she
  // has written none.
  narrative: z.string().nullable().optional(),
  // Platform-admin flag. Optional + defaulted: older backends omit it, and a
  // missing flag must read as "not an admin" so the dashboard degrades closed.
  is_platform_admin: z.boolean().optional().default(false),
  // Read-only admin tier (full admins, delegated read-only admins, + every new
  // user for 30 days). Gates the admin SECTION + aggregate tabs; the raw tabs
  // and permission management stay on is_platform_admin. Degrade closed too.
  admin_view: z.boolean().optional().default(false),
  // Expiry of the 30-day new-user admin-view trial; present only inside it.
  admin_view_trial_expires_at: z.string().optional(),
  // Delegable approval authority (system:platform#approver) — gates the
  // Approvals area, which has its own relation rather than an endpoint id.
  can_approve: z.boolean().optional().default(false),
  // The admin areas this caller may open (endpoint instance ids). The dashboard
  // draws its tab strip from this, so a delegated per-endpoint grant is visible
  // to its grantee instead of being enforced-but-unreachable. Degrade closed:
  // an older backend omits it and the caller sees only what their tier allows.
  admin_endpoints: z.array(z.string()).optional().default([]),
  // Every agent this caller may open a session with — the open catalogue plus
  // the restricted ones they hold a grant on. Deliberately NO default: absent
  // means "this response does not say", which the launcher renders as the open
  // catalogue, while `[]` means "you may open nothing". A default would make an
  // older backend, or any response that simply doesn't carry the field, read as
  // the second.
  launchable_agents: z.array(z.string()).optional(),
  // The caller's direct ward grants. Deliberately NO default (see
  // launchable_agents above) — absence means "this response does not say", so
  // toUser vs toUserPatch (frontend/src/api/types.ts) can tell "no
  // memberships" (`[]`) apart from "not carried by this response".
  location_memberships: z.array(LocationMembershipSchema).optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const AuthResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.literal("bearer"),
  user: UserResponseSchema,
  // admin_view fields live at the TOP LEVEL of AuthResponse (siblings of `user`),
  // NOT inside the nested UserResponse. The backend serialises them here via
  // omitempty — absence means false / null.
  admin_view: z.boolean().optional().default(false),
  admin_view_trial_expires_at: z.string().optional(),
});

// 202 body POST /auth/register returns when email verification is required
// (RequireEmailVerification on). NO access_token/user — the account exists but
// is gated until the emailed link is clicked. Distinguished from the 201
// AuthResponse purely by body shape (see authApi.register), so the existing
// status-agnostic api.post client needs no change.
export const VerificationSentSchema = z.object({
  status: z.literal("verification_sent"),
  email: z.string(),
});

// Discriminated register outcome: either a real session (verification off /
// local) or the "check your inbox" gate (verification on). The `status` literal
// on the 202 body and `access_token` on the 201 body make the two arms
// unambiguous; api.post hands us the parsed body and we branch on it.
export const RegisterResponseSchema = z.union([
  VerificationSentSchema,
  AuthResponseSchema,
]);

// 200 body POST /auth/resend-verification — always generic (no account
// enumeration). The frontend reads only that the call succeeded.
export const ResendVerificationResponseSchema = z.object({
  status: z.literal("ok"),
});

// 200 body POST /auth/verify-email returns for a REPLAY of an
// already-consumed token — no access_token, no session. See
// AlreadyVerifiedResponse in types.ts for why.
export const AlreadyVerifiedSchema = z.object({
  status: z.literal("already_verified"),
});

// Discriminated verify-email outcome: a fresh consumption returns the same
// AuthResponse as login; a replay returns the status-literal body above.
// Same shape as RegisterResponseSchema — the `status` literal vs
// `access_token` fields make the two arms unambiguous.
export const VerifyEmailResponseSchema = z.union([
  AlreadyVerifiedSchema,
  AuthResponseSchema,
]);

export const OAuthAuthorizeResponseSchema = z.object({
  url: z.string(),
  state: z.string(),
});

export const TokenResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.literal("bearer"),
});

// GET /oauth/clients/{client_id} — display identity for the MCP-connector
// consent view ("<client_name> requests access…").
export const OAuthClientInfoSchema = z.object({
  client_id: z.string(),
  client_name: z.string(),
});

// POST /oauth/authorize — the consent decision. The SPA navigates the browser
// to redirect_url (back to the MCP client) for approve and deny alike; the
// server builds it from the validated redirect_uri.
export const OAuthAuthorizeDecisionResponseSchema = z.object({
  redirect_url: z.string(),
});

// ============================================================================
// Tags
// ============================================================================

export const TagInfoResponseSchema = z.object({
  code: z.string(),
  label: z.string(),
  vocabulary: z.string(),
});

// ============================================================================
// Sessions
// ============================================================================

export const SessionResponseSchema = z.object({
  session_kind: z.string().optional(),
  // What this session is a session OF, when it is of something:
  // `module:<id>@<version>` for a run of a module, `curriculum:<id>` for a
  // curriculum run, a continuation handle for a consult. Absent when the
  // session is of nothing, which a plain conversation and a repeatable quiz run
  // both are — so optional here is the wire's own shape, not tolerance.
  //
  // It is how a client tells one run from another in its own list, which
  // `session_kind` cannot: every module run classifies the same.
  resource_key: z.string().optional(),
  id: z.string(),
  title: z.string(),
  message_count: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  tags: z.array(z.string()),
  tag_info: z.array(TagInfoResponseSchema).nullable().optional(),
  archived_at: z.string().nullable().optional(),
  agent_slug: z.string().default("chat"),
  // The session's assistant as a house-standard Reference. `reference` is
  // present only while that agent still exists; a retired agent leaves
  // `{display: "<slug>"}` alone. Optional so a response from a backend that
  // predates the field still parses (the UI then treats it as unresolved-
  // unknown rather than blanking the session).
  agent: ReferenceSchema.optional(),
  // Present iff this session's agent has `speaks_first` configured (e.g. the
  // twin's welcome session, the onboarding interview): the id of the
  // auto-enqueued opening turn (same hidden-note mechanism as the welcome
  // turn). The client registers this in usePendingTurnsStore right after
  // session creation so a turn it never itself POSTed still gets the normal
  // typing-indicator + failure-toast + reload-catch-up coverage. Absent for
  // every ordinary session.
  opening_turn_id: z.string().optional(),
  state: z.unknown().nullable().optional(),
  parent_session_id: z.string().nullable().optional(),
  // Denormalized assigned-patient id (null = personal/unassigned session). Drives
  // patient-grouping in the handover workspace; written with the OpenFGA edge.
  patient_id: z.string().nullable().optional(),
  // Creator display name — drives the cross-nurse "von <colleague>" byline on
  // sessions surfaced via a shared patient (is_shared === true).
  author_name: z.string().nullable().optional(),
  // Creator (planner) user id. `author_id === current user` ⇒ owner (edit/chat);
  // otherwise the session was shared TO this user (read-only, enforced
  // server-side by sessionEditor). Declared here so the strict object() keeps it
  // — without this entry the backend's author_id is stripped on parse. Optional:
  // older backends omit it; consumers degrade to treating the viewer as author.
  author_id: z.string().nullable().optional(),
  // True when the session reached this user via a share (vs. created by them).
  // The TS type (SessionResponse.is_shared) already declared this, but the
  // strict object() was dropping it on parse — declare it so the field actually
  // survives validation and reaches consumers (e.g. the roster Home sections).
  is_shared: z.boolean().optional(),
  // Present only on GET /sessions?q= search results: ts_headline excerpt of the
  // newest matching message + that message's timestamp (snippet absent for
  // title-only matches).
  snippet: z.string().optional(),
  matched_at: z.string().optional(),
  // Server-generated conversation-card fields (card-summarization pass). topic +
  // result_summary + primary_source are absent until the pass first runs;
  // conversation_state defaults to 'active' so a pre-summary or rolled-back
  // backend still renders a valid card instead of failing the whole parse.
  topic: z.string().nullable().optional(),
  result_summary: z.string().nullable().optional(),
  primary_source: z.object({ label: z.string(), ref: z.string() }).nullable().optional(),
  conversation_state: z.enum(['active', 'done', 'needs_review']).default('active'),
  // What the nurse opened this conversation for, in her own words. Declared so
  // the object keeps it on parse: an undeclared key is stripped, and a field
  // the backend sends but no consumer can read is a feature that does not
  // exist. No .default(''), because "no goal stated" is absence, and defaulting
  // it here would answer, at the parse boundary, a question only the nurse can.
  goal: z.string().optional(),
});


// ============================================================================
// Citations
// ============================================================================

export const CitationResponseSchema = z.object({
  document: z.string(),
  page_start: z.number(),
  page_end: z.number(),
  cited_text: z.string(),
  source_type: z.string().default("document"),
  url: z.string().nullable().optional(),
  source_tier: z.number().nullable().optional(),
  retrieval_score: z.number().nullable().optional(),
  // No .default() — an unmarked citation must stay unmarked. Defaulting it to
  // either real value would decide, at the parse boundary, a question only the
  // backend can answer.
  provenance: z.enum(["", "retrieved", "cited"]).optional(),
  // Optional with no .default() for the same reason: absent is a row that
  // predates the field, "" is a citation that genuinely names no chunk, and
  // neither may be turned into an id that resolves to nothing.
  chunk_id: z.string().optional(),
  // Optional for the reason chunk_id is: `messages.citations` is returned to
  // the client verbatim, so every row written before this field existed lacks
  // the key. Requiring it would fail the whole GET /sessions/{id} parse on
  // history the backend cannot rewrite.
  document_id: z.string().optional(),
});

// Response of GET /sessions/{id}/summary (resource-summary spine).
// citations is always an array on the wire ([] not null), but tolerate
// null/absent defensively → [].
export const ResourceSummaryResponseSchema = z.object({
  // The only consumer (handoffApi.getSessionSummary) hits the SESSION summary
  // endpoint; pin the literal so a wrong-endpoint / forward-compat drift surfaces
  // as a parse error rather than silently flowing a mismatched summary through.
  resource_type: z.literal('session'),
  resource_id: z.string(),
  // The session's own agent_slug — its routing identity. The live-summary card
  // opens the child on the right app/subdomain with it (generic to every agent),
  // so it must never be hardcoded to one specialist. Optional+default '' so a
  // backend rollback to a pre-agent_slug version doesn't fail the parse and blank
  // the whole card — the summary still renders; only the deep-link routing
  // degrades to the main app for that brief window.
  agent_slug: z.string().optional().default(''),
  summary: z.string(),
  citations: z
    .array(CitationResponseSchema)
    .nullish()
    .transform((c) => c ?? []),
});
export type ResourceSummaryResponse = z.infer<typeof ResourceSummaryResponseSchema>;

// ============================================================================
// Content Blocks (Agentic UI)
// ============================================================================

const TextBlockSchema = z.object({
  type: z.literal("text"),
  content: z.string(),
});

const ClarificationOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().nullable().optional(),
});

// Wire shape is `{type, data: {…}}` — matches api-service-go's universal
// ContentBlock envelope (`api-service-go/types.go::ContentBlock` —
// `Data json.RawMessage`, decoded per `Type`). Previous flat-shape was a
// drift bug that crashed `e.options.map` once the agent actually emitted
// a `<ui_select>` widget (2026-05-22 bedside repro).
const ClarificationBlockSchema = z.object({
  type: z.literal("clarification"),
  data: z.object({
    id: z.string().optional(),
    question: z.string(),
    options: z.array(ClarificationOptionSchema),
    style: z.union([
      z.literal("chips"),
      z.literal("buttons"),
      z.literal("dropdown"),
    ]),
    allow_multiple: z.boolean().default(false),
  }),
});

// Wire shape is `{type, data: {…}}` — see ClarificationBlockSchema for
// the rationale; same drift class.
const ConfirmationBlockSchema = z.object({
  type: z.literal("confirmation"),
  data: z.object({
    id: z.string().optional(),
    title: z.string(),
    message: z.string(),
    confirm_label: z.string().default("Confirm"),
    cancel_label: z.string().default("Cancel"),
    // Whatever the model declared, unvalidated and undefaulted.
    //
    // A union of the two known values would reject anything else and fail the
    // whole message block, which is the same guard moved to the client and
    // costs the nurse the message over a prompt defect. The vocabulary is
    // stated in the chat agent's manifest and belongs to an evaluation.
    // ConfirmationBlock.tsx tests `=== 'high'`, so an unknown value renders
    // plainly on its own.
    risk_level: z.string().optional(),
  }),
});

const InputFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  field_type: z.union([
    z.literal("text"),
    z.literal("number"),
    z.literal("date"),
    z.literal("select"),
  ]),
  required: z.boolean().default(true),
  placeholder: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  min_value: z.number().nullable().optional(),
  max_value: z.number().nullable().optional(),
  options: z.array(z.string()).nullable().optional(),
});

// Wire shape is `{type, data: {…}}` — see ClarificationBlockSchema for
// the rationale; same drift class.
const InputFormBlockSchema = z.object({
  type: z.literal("input_form"),
  data: z.object({
    id: z.string().optional(),
    form_title: z.string(),
    fields: z.array(InputFieldSchema),
    explanation: z.string(),
    submit_label: z.string().default("Submit"),
  }),
});

// There is no `schedule` block, and there is no shift_commands block.
//
// A care plan used to arrive as a `schedule` block on every agent turn, with a
// version the model incremented itself, and every client folded the transcript
// to find the newest one. A care plan is a plan in tricorder-planner now, read
// through `/plans`, so a plan in a chat log is no longer plan state. The roster went the same way: it was a fenced command log folded
// out of this transcript and it is `GET /shifts` now.
//
// Both are gone from BOTH lists here: the discriminated union and
// KNOWN_CONTENT_BLOCK_TYPES. Removing a type from one and not the other is how
// the renderer and the parser come to disagree — the union decides whether the
// response parses at all, the set decides whether a malformed instance fails
// loud. A block of either type from an older backend takes the
// forward-compatible unknown arm and renders as unsupported content, which is
// the correct answer for a mechanism that no longer exists.

// Allocation proposal block (the allocation agent's patient→nurse pairs).
// `data` stays `unknown` at the wire layer; AllocationProposalCard narrows it
// with isAllocationCommands before rendering — same pattern as the access
// block below.
const AllocationCommandsBlockSchema = z.object({
  type: z.literal("allocation_commands"),
  data: z.unknown(),
});

// Access-command block (the team-access agent's grant/revoke/approve/deny
// proposals). `data` stays `unknown` at the wire layer; foldAccess narrows it
// with isAccessCommands before rendering AccessProposalCards — same pattern as
// allocation_commands. Must be registered in the union AND the set below, or a
// team-access session that has proposals fails the whole GET /sessions parse.
const AccessCommandsBlockSchema = z.object({
  type: z.literal("access_commands"),
  data: z.unknown(),
});

// Profile-field extraction block — emitted by the agentic onboarding
// interview's profile_extractor post-processor, one per assistant turn,
// CUMULATIVE (a later block carries the full picture so far, not a delta).
// The frontend has no reader for it: the onboarding agent writes the profile
// itself through PUT /me, so `data` stays `unknown` at the wire layer, the
// same as allocation_commands and access_commands above. Must be registered
// in the union AND the set below, or an onboarding session fails the whole
// GET /sessions parse.
const ProfileFieldsBlockSchema = z.object({
  type: z.literal("profile_fields"),
  data: z.unknown(),
});

// Protocol step block — class-agnostic protocol runtime renderer.
// Canonical Go source: api-service-go/protocol_dispatcher.go::ProtocolStepPayload.
// Wire shape locked by specs/wire-shapes/protocol_step_block.json.
//
// `.strict()` is NOT applied to ProtocolStepBlockDataSchema because the `state`
// field carries opaque protojson that may gain new sub-fields at the proto level
// without a frontend release. Strict validation would reject otherwise-valid
// payloads from newer backend versions.

// Observation input value-type literal set — see `ProtocolObservationValueType`
// in ./types.ts. Renderer maps each value to its input widget and validator.
//
// Wire schema is `z.string().optional()` (not the literal union) so that a
// future backend `value_type: "date_time"` (or any other forward-compat enum
// addition) does NOT explode the whole ProtocolNodeActionSchema parse — the
// renderer's `resolveValueType` fallback in ProtocolStepBlock.tsx maps any
// unknown string to the safe "string" widget. The literal union below is kept
// for reference (and the `ProtocolObservationValueType` type-alias in
// ./types.ts pins the canonical set), but it's intentionally NOT used at the
// parse boundary.
const ProtocolNodeActionSchema = z.object({
  id: z.string(),
  label: z.string(),
  kind: z.string().optional(),
  // OBSERVATION-only optional fields. Schema accepts them on all actions because
  // the wire is structural — the renderer ignores them for non-INPUT kinds.
  // value_type is `z.string()` (not the literal union) for forward-compat:
  // a future backend value_type the frontend doesn't know about must still
  // parse so the renderer can apply its safe-default fallback.
  value_type: z.string().optional(),
  payload_key: z.string().optional(),
  unit: z.string().optional(),
  placeholder: z.string().optional(),
});

// Tone enum mirror — see `ProtocolTone` in ./types.ts and
// `protocolpb.Tone` in tricorder-protos/proto/tricorder/protocol/v1/protocol.proto.
// Backend omits the field on TONE_UNSPECIFIED, but the literal must
// still be accepted in case a future serialiser writes it explicitly.
const ProtocolToneSchema = z.union([
  z.literal("TONE_UNSPECIFIED"),
  z.literal("TONE_OK"),
  z.literal("TONE_WARN"),
  z.literal("TONE_BAD"),
  z.literal("TONE_MUTED"),
]);

const ProtocolNodeContentSchema = z.object({
  title: z.string(),
  // body and actions are REQUIRED, and not `.optional()`: the dispatcher
  // always serialises both, empty for a node that has none. Optional here
  // means a payload missing `actions` parses fine and renders as a step with
  // no way forward — the nurse sees a card that does nothing and no reason
  // why. Required makes a malformed payload fail loudly at the boundary,
  // which is where it can still be reported.
  body: z.array(z.string()),
  actions: z.array(ProtocolNodeActionSchema),
  tone: ProtocolToneSchema.optional(),
  verdict: z.string().optional(),
  reference: z.string().optional(),
});

export const ProtocolStepBlockDataSchema = z.object({
  protocol_id: z.string(),
  protocol_version: z.string(),
  node_id: z.string(),
  node_kind: z.string().optional(),
  node_content: ProtocolNodeContentSchema.optional(),
  state: z.unknown(),
});

export const ProtocolStepBlockSchema = z.object({
  type: z.literal("protocol_step"),
  data: ProtocolStepBlockDataSchema,
});

// Training-step block — the training-domain twin of protocol_step (see above):
// the training runtime reuses the protocol schema unchanged, so the payload
// shape is identical apart from `component_id`/`component_version` in place
// of `protocol_id`/`protocol_version`. Canonical Go source (mirrors
// api-service-go/protocol_dispatcher.go::ProtocolStepPayload): the training
// step handler's TrainingStepPayload. Never rendered as an interactive inline
// card;
// registering it here only keeps a session containing a training_step message
// from failing Zod validation wholesale (same H6/forward-compat reasoning as
// protocol_step) — see UIComponentRenderer.tsx's `training_step` case.
export const TrainingStepBlockDataSchema = z.object({
  // The wire field names are protocol_id/protocol_version, NOT component_*:
  // the training runtime persists its step audit through the SHARED
  // protocol-step writer (persistProtocolStepWithAudit), because a training
  // component IS a protocol graph. Requiring component_* made every learn
  // session that contains a quiz step fail validation wholesale — the exact
  // failure this block was registered to prevent — so the chat rendered
  // "Could not load session." for any trainee who had taken a quiz.
  protocol_id: z.string(),
  protocol_version: z.string(),
  node_id: z.string(),
  node_kind: z.string().optional(),
  node_content: ProtocolNodeContentSchema.optional(),
  state: z.unknown(),
});

export const TrainingStepBlockSchema = z.object({
  type: z.literal("training_step"),
  data: TrainingStepBlockDataSchema,
});

// Carried at blocks[0] on an inbound_summary message (the specialist-session
// hand-off rendered by InboundSummaryCard). Without this arm in the discriminated
// union below, GET /sessions/{id} would fail Zod validation on any session that
// contains a hand-off message and the whole parent chat would refuse to load.
export const InboundSummaryBlockSchema = z.object({
  type: z.literal("inbound_summary"),
  child_session_id: z.string(),
  child_agent_slug: z.string(),
});


// MCP-UI resource block — class-agnostic; emitted by the
// `mcp_ui_extractor` post-processor for any MCP tool result that
// returns a UIResource. The schema validates the discriminator and
// the `data` shape; the renderer then dispatches on `mimeType`.
//
// **Canonical source-of-truth:** Go side at
// `tricorder-go/chat/mcp_ui_extractor.go::MCPUIResourceBlockData`
// (cross-language sync is currently manual — keep this Zod schema,
// `src/api/types.ts::MCPUIResourceBlockData`, and the Go struct in
// lockstep when a field changes). PR-1018 review finding 7a.
//
// `text` is the payload as the MCP wire spec defines it (HTML string
// for rawHtml; URL for externalUrl). We don't constrain `text` size
// here — large UI payloads stay valid; the iframe sandbox does the
// security work, not this schema.
const MCPUIResourceBlockSchema = z.object({
  type: z.literal("mcp_ui_resource"),
  data: z.object({
    uri: z.string(),
    mimeType: z.string(),
    text: z.string(),
    tool: z.string().optional(),
  }),
});

// Learn-handoff block — emitted by the chat pipeline's TrainingHandoffBlockStep
// when the start_module tool fired and its component ref resolved against the
// published training components in the protocols table (tricorder-go/protocol/training_handoff_flag.go — a direct
// catalog lookup rather than a same-turn suggestion match, since api-service-go
// is itself the catalog authority here). The renderer (TrainingHandoffCard) spawns
// a child `learn` session and deep-links into apps/learn's quiz runner.
const TrainingHandoffBlockSchema = z.object({
  type: z.literal("training_handoff"),
  data: z.object({
    component_id: z.string(),
    component_version: z.string(),
  }),
});

// Known, fully-typed content blocks.
const KnownContentBlockSchema = z.discriminatedUnion("type", [
  TextBlockSchema,
  ClarificationBlockSchema,
  ConfirmationBlockSchema,
  InputFormBlockSchema,
  AllocationCommandsBlockSchema,
  AccessCommandsBlockSchema,
  MCPUIResourceBlockSchema,
  ProtocolStepBlockSchema,
  InboundSummaryBlockSchema,
  ProfileFieldsBlockSchema,
  TrainingStepBlockSchema,
  TrainingHandoffBlockSchema,
]);

// Every `type` literal registered in KnownContentBlockSchema above. Drift here
// fails SAFE: a type omitted from this set only weakens fail-loud for a MALFORMED
// instance of that known type (it would degrade to the unknown arm instead of
// throwing) — it never breaks a VALID block, which always matches
// KnownContentBlockSchema first.
const KNOWN_CONTENT_BLOCK_TYPES = new Set<string>([
  "text",
  "clarification",
  "confirmation",
  "input_form",
  "allocation_commands",
  "access_commands",
  "mcp_ui_resource",
  "protocol_step",
  "inbound_summary",
  "profile_fields",
  "training_step",
  "training_handoff",
]);

// Forward-compatibility arm. A newer backend may emit a content block whose
// `type` this client predates; an unknown type MUST NOT throw and take the whole
// GET /sessions response down with it (the 2026-06 roster `shift_commands` +
// earlier `inbound_summary`/`administration` "whole session refuses to load"
// outages). An unrecognised block parses into this tolerated pass-through, which
// the renderer shows as an "unsupported content" placeholder. A KNOWN type with
// invalid data is excluded here (it fails the discriminatedUnion AND this refine),
// so malformed known blocks still fail loud — only genuinely-unrecognised types
// degrade, never broken known ones.
const UnknownContentBlockSchema = z
  .object({
    type: z.string(),
    data: z.unknown().optional(),
  })
  .refine((b) => !KNOWN_CONTENT_BLOCK_TYPES.has(b.type), {
    message: "known content block type failed validation",
  })
  // Remap to a sentinel discriminator so the ContentBlock union stays disjoint
  // (a bare `type: string` member would defeat narrowing for every known block).
  // The real wire type survives in `unknownType` for the renderer's warn log.
  .transform((b) => ({
    type: UNKNOWN_BLOCK_TYPE,
    unknownType: b.type,
    data: b.data,
  }));

export const ContentBlockSchema = z.union([
  KnownContentBlockSchema,
  UnknownContentBlockSchema,
]);

// ============================================================================
// Confidence Metadata
// ============================================================================

const ConfidenceMetadataSchema = z.object({
  tier: z.union([z.literal("high"), z.literal("medium"), z.literal("low")]),
  retrieval_score_top: z.number().nullable(),
  retrieval_score_avg: z.number().nullable(),
  source_count: z.number(),
  source_tier: z.number().nullable(),
  reasoning: z.string(),
  flags: z.array(z.string()),
  // (`has_stale_source` was removed when source-doc freshness was
  // untangled from the confidence axis — the freshness cue now lives
  // in the MCP-UI card's citation footer, scoped to the specific
  // verdict, instead of a chat-level badge that fired on every
  // authoritative answer past a review-cycle clock. See
  // tricorder-go/chat/confidence.go's package comment + ADR-003 §A2.)
});

// ============================================================================
// Messages
// ============================================================================

const ColleagueExpertResponseSchema = z.object({
  name: z.string(),
  specialty: z.string(),
  years_experience: z.number(),
});

export const MessageResponseSchema = z.object({
  id: z.string(),
  session_id: z.string(),
  // Who WROTE this message. A person's id for anything typed; empty for
  // anything the PLATFORM composed on an agent's behalf (a delegated question,
  // a delegated answer, a bootstrap note). Load-bearing for rendering, not
  // decoration: the delegation envelope is text a nurse can type, so
  // authorship is the only thing that separates an agent's words from a person
  // imitating them. Without this entry zod strips it and every renderer has to
  // assume.
  author_id: z.string().nullable().optional(),
  role: z.union([
    z.literal("user"),
    z.literal("assistant"),
    z.literal("agent"),
    z.literal("system"),
  ]),
  content: z.string(),
  // Layer A RETIRED (Agentic UI Fabric P0): blocks[].type is the ONLY content
  // discriminator. content_type stays on the wire as a FREE string rendering
  // hint (MessageBubble's structured-envelope gate, event messages) — a value this client predates MUST parse instead of 422-ing
  // the whole GET /sessions response (the two-place footgun this kills).
  content_type: z.string().default("text"),
  /** Event-message data. Absent on conversational messages. */
  payload: z.unknown().nullable().optional(),
  citations: nullish(z.array(CitationResponseSchema).optional()),
  colleague_suggestions: nullish(
    z.array(ColleagueExpertResponseSchema).optional(),
  ),
  created_at: z.string(),
  latency_ms: nullish(z.number().optional()),
  content_summary: z.string().nullable().optional(),
  content_details: z.string().nullable().optional(),
  image_url: z.string().nullable().optional(),
  blocks: z.array(ContentBlockSchema).nullable().optional(),
  // The CALLER's own rating, null when they have not rated it. Never the
  // message's: a message has no rating, a person's rating of a message does,
  // and two nurses on one shared session hold two. Always present on the wire.
  feedback_type: z.string().nullable(),
  confidence: ConfidenceMetadataSchema.nullable().optional(),
  prompt_version: z.string().nullable().optional(),
});


// ============================================================================
// Session Query Logs
// ============================================================================

// One row of GET /query-logs — the execution record of one assistant turn.
// `id` === the message id (the merge key the admin Session Monitor joins on to
// show tools-fired per message).
//
// ONE representation for every caller: the row is the same whether it arrives
// via ?session_id= (session gate) or unfiltered (admin gate). What differs is
// which ROWS come back, never which fields. `response_text` is on neither:
// narrowing a row set is free, widening a column is not.
export const QueryLogResponseSchema = z.object({
  id: z.string(),
  // The owning session. Never empty — the column is NOT NULL and the resource
  // has no sessionless member.
  session_id: z.string(),
  query_text: z.string(),
  citation_count: z.number(),
  // null when the turn was never rated, which is a different thing from a
  // rating of "negative".
  feedback: z.string().nullable(),
  created_at: z.string(),
  tools_used: z.array(z.string()),
  source_types: z.array(z.string()),
  // Tool CALLS the turn issued (>= 1). Rows written before
  // db/migrations/api/055_query_logs_tool_call_counts.sql hold tool ROUNDS.
  tool_iterations: z.number(),
  // Per-tool breakdown of that total, keyed by tool name — the multiplicity
  // `tools_used` loses (llm-service-go deduplicates it by name). null on a row
  // written before the migration above: those turns never recorded per-tool
  // counts, which is why this is nullable rather than defaulted to {} — an
  // empty object would claim the turn called no tool.
  tool_call_counts: z.record(z.string(), z.number()).nullable(),
  latency_ms: z.number(),
});

// The collection, in the one list envelope.
export const QueryLogListResponseSchema = pageSchema(QueryLogResponseSchema);

// Response of GET /sessions/{id}/prompt — the session's system prompt broken
// down by the layer that contributed each part. Read-only transparency/audit
// surface; ACL-gated by the same /sessions guard (≥viewer).
//
// Every prompt is rebuilt every turn, so the endpoint rebuilds the breakdown
// at read time, for the caller, the way their next turn would compile it. The
// endpoint returns the real per-layer breakdown or an honest error — there is no "available" maybe-flag, and never an empty or
// placeholder breakdown.
//
// `layer` is left as a free string (not an enum): the backend may add layers
// (deployment, patient_context, …) without a frontend change, and the UI maps
// known names to friendly labels and Title-cases the rest.
export const PromptLayerSchema = z.object({
  layer: z.string(),
  version: z.string(),
  content: z.string(),
});

export const SessionPromptResponseSchema = z.object({
  session_id: z.string(),
  agent_slug: z.string(),
  // Composite version tag, e.g. "base:1.27.0|role:3.0.0|...". May be empty or
  // absent (the backend omits it when no version was recorded).
  version: z.string().optional(),
  // Short audit id of the assembled prompt; null when not recorded.
  prompt_hash: nullish(z.string()),
  // The real per-layer breakdown — always present (the endpoint 5xxs rather than
  // return an empty or placeholder breakdown).
  layers: z.array(PromptLayerSchema),
});

// ============================================================================
// App Config
// ============================================================================

const LocalizedStringsSchema = z.object({
  de: z.string(),
  fr: z.string(),
  en: z.string(),
});

const BrandingConfigSchema = z.object({
  name: LocalizedStringsSchema,
  tagline: LocalizedStringsSchema,
});

const ProfileFieldOptionSchema = z.union([
  z.string(),
  z.object({
    label_key: nullish(z.string().optional()),
    label: nullish(z.string().optional()),
    value: z.union([z.string(), z.number()]),
  }),
]);

// One profile field's option vocabulary. It used to live under
// `onboarding.questions` and carry the scripted interview's prompt key and
// required flag beside the options; the interview is the onboarding module's
// content now, and what a nurse editing her profile still needs is the
// vocabulary.
const ProfileFieldOptionsSchema = z.object({
  field: z.string(),
  type: z.union([
    z.literal("text"),
    z.literal("chips"),
    z.literal("chips_with_freetext"),
    z.literal("range_buttons"),
  ]),
  // Always an array: the server seeds `[]` for a field with no options, so
  // this carries no absent/null/empty ambiguity.
  options: z.array(ProfileFieldOptionSchema),
  default_value: nullish(z.union([z.string(), z.number()]).optional()),
  editable: z.boolean(),
});

const AnalyticsEventSchema = z.object({
  name: z.string(),
  description: z.string(),
});

const AnalyticsConfigSchema = z.object({
  events: z.array(AnalyticsEventSchema),
  export_query_log_limit: z.number(),
});

const FrontendCacheConfigSchema = z.object({
  sessions_stale_ms: z.number(),
  session_detail_stale_ms: z.number(),
  users_stale_ms: z.number(),
});

// Server-enforced input limits mirrored by UI guards (char counters).
const LimitsConfigSchema = z.object({
  max_feedback_comment_length: z.number().int().positive(),
});

// Patient registration config (served by GET /config).
// There is no field list here any more: which attributes exist is a property of
// (resource × profession × unit), which no deployment-wide block can state —
// the form reads GET /patients/$schema instead. `label_format` is the
// template used to render a patient's display label (e.g. "{ward}-{bed}") —
// its `{ward}` placeholder resolves from PLACEMENT, never from `attributes`
// (ward was removed as an attribute).
const PatientsConfigSchema = z.object({
  // Location kinds the unit picker offers (config-driven, never hardcoded in
  // the form). Optional/defaulted for resilience during rollout.
  ward_kinds: z.array(z.string()).optional().default([]),
  label_format: z.string(),
  // admission_ref pseudonymisation vocabulary (design §2/§4). Wire path is
  // `patients.admission_ref.disclosure_reason_codes` (api-service-go
  // PatientsAdmissionRefConfig.DisclosureReasonCodes) — the config-driven
  // disclosure reason-code dropdown for the reveal endpoint's request body;
  // never hardcode this list in the frontend. The whole `admission_ref`
  // block stays optional so a rolling deploy where the backend hasn't
  // shipped it yet degrades gracefully (absence, not invalidity — see the
  // POLICY comment on AppConfigResponseSchema) instead of failing the WHOLE
  // `patients` block's strict-parse. A PRESENT-but-malformed admission_ref
  // still fails the whole parse, same as every other block — there is no
  // silent per-block substitution anywhere in this schema. Deliberately NO
  // `.default([])` on the inner array:
  // an absent/empty vocabulary must read as `undefined`/`[]` and be
  // rendered as an explicit "disclosure reasons unavailable" error state by
  // the reveal dialog (AdmissionRefSection.tsx) — silently coalescing it
  // here would produce a picker that LOOKS functional but 422s on every
  // submit with no visible cause.
  admission_ref: z
    .object({
      disclosure_reason_codes: z.array(z.string()).optional(),
    })
    .optional(),
});

// GET /patients/{id} `admission_ref_link` metadata AND the POST
// /patients/{id}/admission-ref/link response share this shape — plain
// strings/booleans only, no link content, so no enum-leak risk like the
// disclosure schema below; validated for the same wire-drift-catches-early
// reason `getLocationPatientSchema` already validates its response.
export const AdmissionRefLinkSchema = z.object({
  strategy: z.string(),
  linked: z.boolean(),
  linked_at: z.string().optional(),
  // Optional because a LIST item never carries it: the check costs one ACL
  // round trip per patient, so a page does not pay it. No `.default(false)`
  // — false would mean "you may not disclose", which is a different answer
  // from "nobody asked", and the reveal affordance gates on this.
  can_disclose: z.boolean().optional(),
});

// POST /patients/{id}/admission-ref/disclosures response (design §4 — "THE
// one door" that ever carries the plaintext admission link). `link` is a
// plain `z.record(z.string(), z.string())` — deliberately NO z.enum/
// z.literal anywhere in this subtree: Zod's invalid_enum_value issue
// embeds the RECEIVED value in its message, and parseResponse's failure
// path logs those issue messages — an enum here could leak link plaintext
// to the console/log drain on a mere shape mismatch. Keys are whatever
// link_fields the ward declared (schema-driven, not a known set), so a
// record is also the structurally correct shape, not just the safer one.
export const DiscloseAdmissionRefResponseSchema = z.object({
  link: z.record(z.string(), z.string()),
  disclosed_at: z.string(),
});

// Config-driven stay vocabulary, served by GET /config `stay` block
// (api-service-go StayConfig). Drives the class/disposition pickers in the
// Patient Administration app. `presences` is the CLINICAL presence axis and is
// deliberately separate from `statuses`: "the ward has admitted this patient"
// and "this patient is on the ward right now" are two facts.
const StayConfigSchema = z.object({
  classes: z.array(z.string()),
  default_class: z.string(),
  statuses: z.array(z.string()),
  presences: z.array(z.string()),
  dispositions: z.array(z.string()),
  dispositions_requiring_destination: z.array(z.string()),
  event_types: z.array(z.string()),
});

// POLICY for every block below: additive/backward-compat fields are
// `.optional()` — an OLDER backend that never sends a block yet is a
// legitimate deployment state, and the consumer supplies its own client-side
// default for genuine absence (e.g. query-config.ts's `config?.frontend_cache
// ?? DEFAULT_FRONTEND_CACHE`). A block that IS present but does NOT match its
// schema is a real backend contract break, not a rolling-deploy gap — it MUST
// fail the whole `AppConfigResponseSchema.safeParse`, which useConfigLoader
// turns into a visible ConfigErrorScreen with retry (App.tsx). There is no
// per-block silent degrade (formerly `resilientBlock`, a `schema.catch(default)`
// that logged a warning and substituted a fabricated value): "logged" is not
// "visible" to the person it happens to, and it reproduces exactly the bug
// class this file's `organisation_ref` / `location_memberships` work exists to
// fix — a real contract drift in `org` silently disabling onboarding
// self-assignment, witnessed only by a console warning nobody was watching
// (the 2026-06-07 outage this used to cite as its OWN justification was
// actually the opposite lesson: a real backend defect needs a LOUD failure
// with retry, which now exists — see ConfigErrorScreen — not a quieter one).
export const AppConfigResponseSchema = z.object({
  version: z.string(),
  // Optional: `config/app.yaml` `branding.name` is the canonical source served
  // by the backend. useBrandName() resolves config-first, i18n-fallback
  // (`app.name`) — the i18n fallback is what actually renders when /config is
  // unavailable, since a failed fetch surfaces ConfigErrorScreen rather than a
  // substitute config object. The DEV preview harnesses' synthetic config
  // (preview/previewConfig.ts) also omits branding, exercising this same path.
  branding: BrandingConfigSchema.optional(),
  language: z.object({
    default: z.string(),
    supported: z.array(z.string()),
    detection: z.union([z.literal("browser"), z.literal("explicit")]),
  }),
  profile: z.object({
    fields: z.array(ProfileFieldOptionsSchema),
  }),
  // The tenant's activation policy: the module every new account is started on.
  // `""` is a real answer meaning this deployment declares none, which is the
  // shipped default, so the key is present and the value may be empty.
  //
  // Parsed and read by nothing: the onboarding gate that used it died with the
  // activation run, and every sign-in now lands on home (App.tsx,
  // OAuthCallback.tsx).
  //
  // Optional as a block: api-service and the PWA do not deploy together, and a
  // /config served by the previous image carries no such key.
  activation: z.object({ module_ref: z.string() }).optional(),
  // No runtime consumer reads this on the bedside path — telemetry only.
  // Optional: an older backend that never sends it is a legitimate absent
  // state (query-config.ts has no consumer wired to it yet either). A
  // PRESENT-but-malformed block fails the whole parse.
  analytics: AnalyticsConfigSchema.optional(),
  // NOTE: /config does not serve a `features` block — there is no `features:`
  // key in config/app.yaml and no Features field on the backend's AppConfig.
  // Nothing in the frontend has ever read a flag from it (audited 2026-07-10),
  // so it is not parsed here either way — zod strips unknown keys.
  // TanStack Query stale-time tunables. query-config.ts's getStaleConfig()
  // already reads `config?.frontend_cache ?? DEFAULT_FRONTEND_CACHE` — an
  // older backend that omits this block is the legitimate absent case that
  // default serves; a malformed one fails the whole parse.
  frontend_cache: FrontendCacheConfigSchema.optional(),
  // UI input guards — the server still enforces these regardless of what the
  // frontend does with them. No consumer reads this yet.
  limits: LimitsConfigSchema.optional(),
  // KB browsing chrome — already fully optional internally.
  knowledge_base: z.object({
    base_url: z.string().optional(),
    documents: z.array(z.unknown()).optional(),
    categories: z.array(z.object({
      key: z.string(),
      label_en: z.string(),
      label_de: z.string(),
    })).optional(),
  }).optional(),
  patients: PatientsConfigSchema.optional(),
  // Config-driven stay vocabulary — drives the Patient Administration app's
  // class/disposition pickers. Non-clinical vocabulary (not the
  // patient-registration path itself, which stays under `patients` above).
  //
  // REQUIRED, unlike the additive blocks below. This block was renamed from
  // `encounter` when the table became `stays`; it has never been genuinely
  // absent, so `.optional()` here does not describe a rolling-deploy gap, it
  // just converts a name mismatch into `undefined`. That is what happened: the
  // backend emitted `stay`, this line kept reading `encounter`, and the admit
  // button went dead and the disposition picker went empty with no error
  // anywhere. A missing `stay` block is a backend contract break and must fail
  // the parse into the visible ConfigErrorScreen, per this file's policy below.
  stay: StayConfigSchema,
  // Launchable agent (app) catalogue — drives the new-session agent picker.
  // Config-driven end to end (backend populates it from tricorder-agent's
  // catalogue),
  // so a new agent appears in the picker without a frontend release; never
  // hardcode an agent list in the frontend. Optional: an older backend or a
  // deployment without the chat subsystem legitimately omits it — the picker
  // then offers only the default new-session action.
  agents: z.array(
    z.object({
      slug: z.string(),
      name: z.string(),
      // Single DNS label of the agent's sub-app host (sessionRouting
      // builds the cross-app URL from it); absent for main-app agents.
      subdomain: z.string().optional(),
      // Agent-identity fields (config-sourced from the agent manifests). Each
      // carries a default so an older backend that omits them (on an agent
      // ENTRY that IS present) reads as a neutral identity rather than
      // failing the whole agents array over one missing cosmetic field.
      scope: z.enum(['patient', 'personal', 'team']).default('personal'),
      accent: z.string().default('neutral-700'),
      icon: z.string().default('Sparkles'),
      // Whether the agent is closed to the general population. Says THAT,
      // never who — the per-caller answer is GET /me launchable_agents.
      // Default false (open) for an older backend that omits it.
      restricted: z.boolean().default(false),
    }),
  ).optional(),
  // CUJ-84 org-hierarchy vocabulary (ADMIN-ONLY — the Organisation console's
  // config-driven kind / resource-type pickers + the onboarding self-assign
  // toggle, `org.self_assign` — the exact guardrail the profile-org-derived
  // ward bug lives behind). Both vocab consumers (useLocationKinds /
  // useKnownResourceTypes) already read via `config?.org?.…` optional
  // chaining, so an ABSENT block (older backend) degrades the admin picker to
  // empty cleanly. A PRESENT-but-malformed org block is a real contract
  // break and must fail the whole parse — see the module-level POLICY comment
  // above for why silently disabling self_assign here (the former
  // resilientBlock behaviour) is exactly the bug class this file exists to
  // close, not a resilience feature. known_resource_types + self_assign stay
  // .optional() WITHIN a present block for the same additive-field reason.
  org: z.object({
      location_kinds: z.array(z.string()),
      // Hard cap the org list endpoints clamp `limit` to (app.yaml
      // org.max_page_size). The orgApi full-set walkers page at exactly this
      // cap; optional so an older backend degrades to the client-side default.
      max_page_size: z.number().optional(),
      known_resource_types: z.array(z.string()).optional(),
      // Onboarding self-assignment guardrail (CUJ-84). The Organisation console
      // reads `role_label` to render the "self-selectable during onboarding"
      // toggle — sourcing the marker key from config (never hardcoding it) keeps
      // the admin control in lockstep with the backend's enforcement key.
      // Onboarding reads the whole block to source its role/location pickers from
      // the respective GET /roles and GET /locations endpoints, filtered by the
      // SAME keys the backend enforces on write (the UI filter is convenience,
      // not the boundary — see api-service-go/onboarding_org.go).
      self_assign: z
        .object({
          enabled: z.boolean(),
          role_label: z.string(),
          // Location kinds a user may self-assign to during onboarding (subset
          // of org.location_kinds). Defaulted for resilience: an older backend
          // omitting it degrades the onboarding location step to "skip".
          location_kinds: z.array(z.string()).optional().default([]),
        })
        .optional(),
    }).optional(),
  // The staffing vocabulary (api-service-go `staffing`) — the team kinds and the
  // grade ladder. zod's default object() strips unknown keys, so this block
  // MUST be declared here or the backend's `staffing` is silently dropped after
  // parse; adding it to types.ts alone is not enough, which is the failure that
  // left the horizon control on its built-in defaults for a release.
  //
  // The SHIFT-TYPE catalogue is deliberately absent. A shift type is the roster
  // plan category's declaration in the planning service, so
  // there is one home for the codes a ward works and it is the planning
  // service's.
  staffing: z
    .object({
      qualifications: z
        .array(z.object({ code: z.string(), label: z.record(z.string(), z.string()) }))
        .optional(),
      team_kinds: z
        .array(z.object({ code: z.string(), label: z.record(z.string(), z.string()) }))
        .optional(),
      staffed_location_kinds: z.array(z.string()).optional(),
      role_qualifications: z.record(z.string(), z.string()).optional(),
    })
    .optional(),
});

// ============================================================================
// Profile Summary
// ============================================================================

// ============================================================================
// Bookmarks
// ============================================================================

const FailureReasonSchema = z.union([
  z.literal("pubmed_timeout"),
  z.literal("pubmed_api_error"),
  z.literal("llm_timeout"),
  z.literal("llm_rate_limit"),
  z.literal("llm_api_error"),
  z.literal("no_enrichment_needed"),
  z.literal("no_results_found"),
  z.literal("invalid_content"),
  z.literal("enrichment_error"),
  z.literal("unknown"),
]);

const EnrichmentMetadataSchema = z.object({
  attempt_count: z.number(),
  max_attempts: z.number(),
  started_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
  duration_ms: z.number().nullable().optional(),
  failure_reason: FailureReasonSchema.nullable().optional(),
  last_error: z.string().nullable().optional(),
  failed_phase: z.union([
    z.literal("orchestrator"),
    z.literal("research"),
    z.literal("synthesis"),
  ]).nullable().optional(),
  retry_after: z.string().nullable().optional(),
  pubmed_searched: z.boolean().default(false),
  sai_searched: z.boolean().default(false),
  queries_used: z.array(z.string()).optional(),
});

const PubMedReferenceSchema = z.object({
  pmid: z.string(),
  title: z.string(),
  authors: z.string().nullable().optional(),
  abstract: z.string().nullable().optional(),
  year: z.string().nullable().optional(),
  url: z.string(),
});

const SAIReferenceSchema = z.object({
  name: z.string(),
  section: z.string().nullable().optional(),
  content_preview: z.string().nullable().optional(),
  score: z.number().nullable().optional(),
});

export const BookmarkResponseSchema = z.object({
  id: z.string(),
  user_id: z.string(), // Bookmark owner (user identity) - keep as user_id
  message_id: z.string(),
  session_id: z.string(),
  // Open set — agent types are config-driven (new agents added via app.yaml
  // without a frontend release). navigateToSession treats unknown types as
  // "render inline", which is the safe default. Do NOT narrow to an enum —
  // that would break the N-agent extensibility from the intent routing design.
  agent_slug: z.string(),
  content_summary: z.string().nullable(),
  content_details: z.string().nullable(),
  citations: nullish(z.array(CitationResponseSchema).optional()),
  enrichment_status: z.union([
    z.literal("pending"),
    z.literal("processing"),
    z.literal("completed"),
    z.literal("failed"),
  ]),
  enriched_at: z.string().nullable().optional(),
  enrichment_metadata: EnrichmentMetadataSchema.nullable().optional(),
  pubmed_references: z.array(PubMedReferenceSchema).nullable().optional(),
  sai_references: z.array(SAIReferenceSchema).nullable().optional(),
  study_notes: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const BookmarkResponsePageSchema = pageSchema(BookmarkResponseSchema);

// ============================================================================
// UIAction wire types — runtime validators
// ============================================================================
// Canonical source-of-truth: `tricorder-protos/proto/tricorder/mcpui/v1/action.proto`.
// Manual TS interfaces in `./types.ts`; fixtures in
// `specs/wire-shapes/ui_action_{request,acknowledgment}.json`. Drift is
// caught by the compile-time assignability checks below + the
// fixture-vs-interface tests added in T2.6.
//
// `params` on tool/intent is the JSON object form of
// `google.protobuf.Struct` — `z.record(z.string(), z.unknown())` matches
// the manual `Record<string, unknown>` type.

export const ToolActionSchema = z.object({
  toolName: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
});

export const IntentActionSchema = z.object({
  intent: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
});

export const PromptActionSchema = z.object({
  prompt: z.string(),
});

export const LinkActionSchema = z.object({
  url: z.string(),
});

export const NotifyActionSchema = z.object({
  message: z.string(),
});

// Wire-faithful: exactly one of five keys is populated (mirrors proto
// `oneof` JSON encoding). No shared discriminator field, so `z.union`
// is the right tool — `z.discriminatedUnion` requires a common literal
// key across all members.
//
// `.strict()` on each member rejects payloads with two or more keys
// populated (which the proto `oneof` contract prohibits). Without it,
// `{ tool: {...}, intent: {...} }` would parse as the first arm and
// silently drop the second key — a real validation hole on
// postMessage payloads from sandboxed iframes.
export const UIActionSchema = z.union([
  z.object({ tool: ToolActionSchema }).strict(),
  z.object({ intent: IntentActionSchema }).strict(),
  z.object({ prompt: PromptActionSchema }).strict(),
  z.object({ link: LinkActionSchema }).strict(),
  z.object({ notify: NotifyActionSchema }).strict(),
]);

// `.strict()` rejects extra top-level keys. Without it, an unknown
// field on the wire would be silently stripped — a fixture-with-extras
// drift would surface as an opaque `toEqual` deep-equality miss in
// the contract tests rather than a descriptive Zod rejection. With
// `.strict()`, drift produces `Unrecognized key(s)` pointing at the
// offending field name.
// Agentic UI Fabric host->agent feedback (fabric.proto SurfaceEvent), POSTed
// on the same /ui_actions round-trip. Manual mirror of SurfaceEventWire.
export const SurfaceEventWireSchema = z.object({
  surfaceId: z.string(),
  surfaceChanged: z.object({ toSurfaceId: z.string().optional() }).optional(),
  stepDone: z.object({ guideId: z.string().optional() }).optional(),
  confirmed: z.object({ guideId: z.string().optional() }).optional(),
  dismissed: z.object({ guideId: z.string().optional() }).optional(),
});

export const UIActionRequestSchema = z.object({
  // All optional: a card action carries sourceBlockUri + action; a fabric
  // SurfaceEvent carries only surfaceEvent (GuideEngine, no source block).
  sourceBlockUri: z.string().optional(),
  sourceTool: z.string().optional(),
  action: UIActionSchema.optional(),
  surfaceEvent: SurfaceEventWireSchema.optional(),
}).strict();

export const UIActionAcknowledgmentStatusSchema = z.union([
  z.literal("STATUS_UNSPECIFIED"),
  z.literal("STATUS_RECEIVED"),
  z.literal("STATUS_OK"),
  z.literal("STATUS_UNAUTHORIZED"),
  z.literal("STATUS_TOOL_FAILED"),
  z.literal("STATUS_VALIDATION_FAILED"),
  z.literal("STATUS_RATE_LIMITED"),
]);

// See the rationale comment above `UIActionRequestSchema` for `.strict()`.
export const UIActionAcknowledgmentSchema = z.object({
  actionId: z.string(),
  status: UIActionAcknowledgmentStatusSchema,
  errorMessage: z.string().optional(),
}).strict();

// ============================================================================
// UIAction SSE event payloads — runtime validators
// ============================================================================
// Wire contract source-of-truth lives in `api-service-go/uiactions/events.go`
// (not proto — these ride stdlib `encoding/json`, see the package doc on
// the Go side). Fixtures: `specs/wire-shapes/ui_action_{started,result,error}_event.json`.
//
// `.strict()` is intentional on every event schema: the SSE wire is locked
// and an unrecognised key on the wire is a contract drift signal we want to
// surface, not silently strip. See `UIActionRequestSchema` above for the
// long-form rationale.

// `block` mirrors `MCPUIResourceBlockSchema.shape.data` — same wire fields,
// same types. We can't reuse the schema directly because that one is a
// `discriminatedUnion` member with `type: 'mcp_ui_resource'` baked in, and
// the SSE payload's `block` carries the inner data shape only.
const UIActionResultBlockSchema = z.object({
  uri: z.string(),
  mimeType: z.string(),
  text: z.string(),
  tool: z.string().optional(),
}).strict();

export const UIActionStartedEventSchema = z.object({
  actionId: z.string(),
  toolName: z.string().optional(),
  sourceBlockUri: z.string(),
  startedAt: z.string(),
}).strict();

export const UIActionResultEventSchema = z.object({
  actionId: z.string(),
  sessionId: z.string(),
  block: UIActionResultBlockSchema.optional(),
}).strict();

export const UIActionErrorEventSchema = z.object({
  actionId: z.string(),
  status: UIActionAcknowledgmentStatusSchema,
  errorMessage: z.string().optional(),
}).strict();

// Compile-time helper: fails to type-check if A and B are not mutually
// assignable. The tuple wrapping (`[A] extends [B]`) prevents distributive
// evaluation on union types; checking both directions catches drift either
// way (TS adds a value, Zod adds a value, type-widening). Note: TS
// structural typing makes optional-field drift invisible to assignment
// — adding an optional field to only one side does NOT fail compilation.
// The fixture-vs-interface tests (T2.6) cover that direction at runtime.
type AssertEqual<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;

const _checkToolAction: AssertEqual<ToolAction, z.infer<typeof ToolActionSchema>> = true;
const _checkIntentAction: AssertEqual<IntentAction, z.infer<typeof IntentActionSchema>> = true;
const _checkPromptAction: AssertEqual<PromptAction, z.infer<typeof PromptActionSchema>> = true;
const _checkLinkAction: AssertEqual<LinkAction, z.infer<typeof LinkActionSchema>> = true;
const _checkNotifyAction: AssertEqual<NotifyAction, z.infer<typeof NotifyActionSchema>> = true;
const _checkUIAction: AssertEqual<UIAction, z.infer<typeof UIActionSchema>> = true;
const _checkUIActionRequest: AssertEqual<UIActionRequest, z.infer<typeof UIActionRequestSchema>> = true;
// Status union gets its own assertion for precise error attribution: if
// only the enum drifts, the error surfaces here rather than buried inside
// the compound _checkUIActionAck check. Bidirectional AssertEqual already
// catches the drift via the parent — this is for clearer diagnostics.
const _checkAckStatus: AssertEqual<UIActionAcknowledgmentStatus, z.infer<typeof UIActionAcknowledgmentStatusSchema>> = true;
const _checkUIActionAck: AssertEqual<UIActionAcknowledgment, z.infer<typeof UIActionAcknowledgmentSchema>> = true;
const _checkUIActionStartedEvent: AssertEqual<UIActionStartedEvent, z.infer<typeof UIActionStartedEventSchema>> = true;
const _checkUIActionResultEvent: AssertEqual<UIActionResultEvent, z.infer<typeof UIActionResultEventSchema>> = true;
const _checkUIActionErrorEvent: AssertEqual<UIActionErrorEvent, z.infer<typeof UIActionErrorEventSchema>> = true;

void _checkToolAction;
void _checkIntentAction;
void _checkPromptAction;
void _checkLinkAction;
void _checkNotifyAction;
void _checkUIAction;
void _checkUIActionRequest;
void _checkAckStatus;
void _checkUIActionAck;
void _checkUIActionStartedEvent;
void _checkUIActionResultEvent;
void _checkUIActionErrorEvent;

// ============================================================================
// Elicitation SSE event — `elicitation_prompt`
// ============================================================================
// Wire shape emitted via stdlib encoding/json (camelCase) by
// api-service-go when an MCP tool calls mcpserver.Elicit.
//
// `.strict()` is intentional: an unrecognised key on the wire is a contract
// drift signal we want to surface rather than silently strip.

export const ElicitationPromptEventSchema = z.object({
  event: z.literal('elicitation_prompt'),
  elicitationId: z.string(),
  message: z.string(),
  requestedSchemaJson: z.string(),
}).strict();

const _checkElicitationPromptEvent: AssertEqual<ElicitationPromptEvent, z.infer<typeof ElicitationPromptEventSchema>> = true;
void _checkElicitationPromptEvent;

// ============================================================================
// Protocol step block — compile-time assignability checks
// ============================================================================
// Canonical Go source: api-service-go/protocol_dispatcher.go::ProtocolStepPayload.
// Drift caught by: specs/wire-shapes/protocol_step_block.json (Go + TS assert).
const _checkProtocolNodeAction: AssertEqual<ProtocolNodeAction, z.infer<typeof ProtocolNodeActionSchema>> = true;
const _checkProtocolNodeContent: AssertEqual<ProtocolNodeContent, z.infer<typeof ProtocolNodeContentSchema>> = true;
const _checkProtocolStepBlockData: AssertEqual<ProtocolStepBlockData, z.infer<typeof ProtocolStepBlockDataSchema>> = true;
const _checkProtocolStepBlock: AssertEqual<ProtocolStepBlock, z.infer<typeof ProtocolStepBlockSchema>> = true;
void _checkProtocolNodeAction;
void _checkProtocolNodeContent;
void _checkProtocolStepBlockData;
void _checkProtocolStepBlock;

// Training-step block — same drift-check discipline as protocol_step above.
const _checkTrainingStepBlockData: AssertEqual<TrainingStepBlockData, z.infer<typeof TrainingStepBlockDataSchema>> = true;
const _checkTrainingStepBlock: AssertEqual<TrainingStepBlock, z.infer<typeof TrainingStepBlockSchema>> = true;
void _checkTrainingStepBlockData;
void _checkTrainingStepBlock;

// ============================================================================
// Agentic UI Fabric (P0) — Directive SSE payloads
// ============================================================================
// protojson (camelCase) encoding of tricorder.fabric.v1.Directive. Every
// field optional: protojson omits unpopulated fields, and an unknown future
// verb must parse as an empty object (the GuideEngine then no-ops).

export const DirectiveWireSchema = z.object({
  surfaceId: z.string().optional(),
  render: z.object({ block: z.record(z.string(), z.unknown()).optional() }).optional(),
  navigate: z.object({ targetGuideId: z.string().optional() }).optional(),
  spotlight: z
    .object({
      guideIds: z.array(z.string()).optional(),
      coachMark: z.string().optional(),
    })
    .optional(),
  prompt: z.object({ text: z.string().optional() }).optional(),
  notify: z.object({ message: z.string().optional() }).optional(),
});

// Payload of a `directive` SSE event (api-service-go/uiactions/events.go
// DirectiveEvent — camelCase stdlib json tags).
export const DirectiveEventSchema = z.object({
  sessionId: z.string(),
  directive: DirectiveWireSchema,
});
